import { useState, useMemo } from 'react';
import {
    Card,
    CardContent,
    Button,
    Badge,
    Input,
} from '@ui';
import { cn } from '@ui/lib/utils';
import {
    Plus,
    FileText,
    PenTool,
    ClipboardList,
    Eye,
    Trash2,
    Edit,
    Copy,
    Image as ImageIcon,
    FileType2,
    Send,
    History,
    Search,
    Filter,
    Download,
} from 'lucide-react';
import type { ConsentTemplate } from '../../types';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
} from 'lucide-react';

// 필터 타입
type FilterType = 'all' | 'mandatory' | 'optional' | 'pdf' | 'image' | 'template';

// 페이지 정보 타입
interface PageInfo {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    pageSize: number;
    isFirst: boolean;
    isLast: boolean;
    hasNext: boolean;
    hasPrev: boolean;
}

interface ConsentTabProps {
    // Template data and handlers
    templates: ConsentTemplate[];
    onCreateTemplate: () => void;
    onEditTemplate: (template: ConsentTemplate) => void;
    onViewTemplate: (template: ConsentTemplate) => void;
    onDeleteTemplate: (template: ConsentTemplate) => void;
    onDuplicateTemplate: (template: ConsentTemplate) => void;
    onSignWithTemplate: (templateId: string) => void;
    onSendConsent: () => void;
    onViewVersionHistory: (template: ConsentTemplate) => void;
    onSetActiveVersion: (templateId: string, version: number) => void;
    // Pagination
    pageInfo?: PageInfo;
    onGoToPage?: (page: number) => void;
    onGoToNextPage?: () => void;
    onGoToPrevPage?: () => void;
    onGoToFirstPage?: () => void;
    onGoToLastPage?: () => void;
}

export function ConsentTab({
    templates,
    onCreateTemplate,
    onEditTemplate,
    onViewTemplate,
    onDeleteTemplate,
    onDuplicateTemplate,
    onSignWithTemplate,
    onSendConsent,
    onViewVersionHistory,
    onSetActiveVersion,
    pageInfo,
    onGoToPage,
    onGoToNextPage,
    onGoToPrevPage,
    onGoToFirstPage,
    onGoToLastPage,
}: ConsentTabProps) {
    // 검색 및 필터 상태
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState<FilterType>('all');

    // 필터링된 템플릿
    const filteredTemplates = useMemo(() => {
        return templates.filter(template => {
            // 검색어 필터
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const matchesName = template.name.toLowerCase().includes(query);
                if (!matchesName) return false;
            }

            // 타입 필터
            switch (filterType) {
                case 'mandatory':
                    return template.isMandatory;
                case 'optional':
                    return !template.isMandatory;
                case 'pdf':
                    return template.isFileUploaded && template.fileType === 'pdf';
                case 'image':
                    return template.isFileUploaded && template.fileType === 'image';
                case 'template':
                    return !template.isFileUploaded;
                default:
                    return true;
            }
        });
    }, [templates, searchQuery, filterType]);

    // 통계 정보 (페이지 정보가 있으면 전체 개수 사용)
    const stats = useMemo(() => ({
        total: pageInfo?.totalElements ?? templates.length,
        mandatory: templates.filter(t => t.isMandatory).length,
        optional: templates.filter(t => !t.isMandatory).length,
    }), [templates, pageInfo]);

    return (
        <div className="space-y-6">
            {/* 동의서 양식 관리 */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100 flex items-center gap-2">
                            <ClipboardList className="h-5 w-5 text-orange-500" />
                            동의서 양식 관리
                        </h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                            고객에게 서명받을 동의서 양식을 관리합니다.
                            <span className="ml-2 text-neutral-400 dark:text-neutral-500">
                                (전체 {stats.total}개 / 필수 {stats.mandatory}개)
                            </span>
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" className="gap-2" onClick={onSendConsent}>
                            <Send className="h-4 w-4" />
                            동의서 발송
                        </Button>
                        <Button className="gap-2" onClick={onCreateTemplate}>
                            <Plus className="h-4 w-4" />
                            새 양식 추가
                        </Button>
                    </div>
                </div>

                {/* 검색 및 필터 */}
                <div className="flex gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 dark:text-neutral-500" />
                        <Input
                            placeholder="양식 이름 또는 설명 검색..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <div className="flex gap-1">
                        {[
                            { value: 'all', label: '전체' },
                            { value: 'mandatory', label: '필수' },
                            { value: 'optional', label: '선택' },
                            { value: 'pdf', label: 'PDF' },
                            { value: 'image', label: '이미지' },
                            { value: 'template', label: '템플릿' },
                        ].map(({ value, label }) => (
                            <Button
                                key={value}
                                variant={filterType === value ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFilterType(value as FilterType)}
                                className={cn(
                                    'text-xs h-9',
                                    filterType === value && 'bg-orange-500 hover:bg-orange-600'
                                )}
                            >
                                {label}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* 검색 결과 없음 */}
                {filteredTemplates.length === 0 && templates.length > 0 && (
                    <div className="p-8 text-center border rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                        <Search className="h-10 w-10 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
                        <p className="text-neutral-500 dark:text-neutral-400">검색 결과가 없습니다</p>
                        <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">다른 검색어나 필터를 시도해보세요</p>
                        <Button
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={() => { setSearchQuery(''); setFilterType('all'); }}
                        >
                            필터 초기화
                        </Button>
                    </div>
                )}

                <div className="grid gap-4">
                    {filteredTemplates.map((template) => (
                        <Card key={template.id} className="transition-all">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-4">
                                    {/* 미리보기 썸네일 또는 기본 아이콘 */}
                                    {template.isFileUploaded && template.fileUrl ? (
                                        <button
                                            type="button"
                                            onClick={() => onViewTemplate(template)}
                                            className={cn(
                                                'relative flex h-20 w-20 items-center justify-center rounded-lg border-2 overflow-hidden transition-all cursor-pointer hover:border-orange-400 hover:shadow-md flex-shrink-0',
                                                template.isMandatory ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20' : 'border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50'
                                            )}
                                            title="클릭하여 미리보기"
                                        >
                                            {template.fileType === 'image' ? (
                                                <img
                                                    src={template.fileUrl}
                                                    alt={template.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="flex flex-col items-center justify-center gap-1 p-2">
                                                    <FileType2 className="h-8 w-8 text-red-500" />
                                                    <span className="text-[9px] font-medium text-red-600 truncate max-w-full">
                                                        {template.fileName || 'PDF'}
                                                    </span>
                                                </div>
                                            )}
                                            {/* 미리보기 힌트 */}
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20 transition-colors">
                                                <Eye className="h-5 w-5 text-white opacity-0 hover:opacity-100 transition-opacity" />
                                            </div>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => onViewTemplate(template)}
                                            className={cn(
                                                'flex h-20 w-20 items-center justify-center rounded-lg transition-colors cursor-pointer flex-shrink-0',
                                                template.isMandatory ? 'bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200' : 'bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200'
                                            )}
                                            title="클릭하여 미리보기"
                                        >
                                            <FileText className={cn(
                                                'h-8 w-8',
                                                template.isMandatory ? 'text-orange-600' : 'text-neutral-400 dark:text-neutral-500'
                                            )} />
                                        </button>
                                    )}

                                    {/* 템플릿 정보 */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-medium text-neutral-800 dark:text-neutral-100">{template.name}</h3>
                                            {template.isFileUploaded && (
                                                <Badge
                                                    variant="outline"
                                                    className={cn(
                                                        'text-xs gap-1',
                                                        template.fileType === 'pdf'
                                                            ? 'border-red-200 bg-red-50 text-red-700'
                                                            : 'border-blue-200 bg-blue-50 text-blue-700'
                                                    )}
                                                >
                                                    {template.fileType === 'pdf' ? (
                                                        <>
                                                            <FileType2 className="h-3 w-3" />
                                                            PDF
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ImageIcon className="h-3 w-3" />
                                                            이미지
                                                        </>
                                                    )}
                                                </Badge>
                                            )}
                                            {!template.isFileUploaded && (
                                                <Badge variant="outline" className="text-xs gap-1 border-green-200 bg-green-50 text-green-700">
                                                    <FileText className="h-3 w-3" />
                                                    템플릿
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                                            <p className="text-xs text-neutral-400 dark:text-neutral-500">
                                                마지막 수정: {new Date(new Date(template.updatedAt).getTime() + 9 * 60 * 60 * 1000).toLocaleDateString('ko-KR', { timeZone: 'UTC' })}
                                            </p>
                                            {/* 버전 선택 드롭다운 */}
                                            {template.versionHistory && template.versionHistory.length > 1 ? (
                                                <div className="flex items-center gap-1">
                                                    <span className="text-[10px] text-neutral-400 dark:text-neutral-500">사용 버전:</span>
                                                    <select
                                                        value={template.activeVersion || template.currentVersion || 1}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            onSetActiveVersion(String(template.id), Number(e.target.value));
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-[10px] h-5 px-1.5 py-0 border rounded bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-orange-500 cursor-pointer"
                                                    >
                                                        {template.versionHistory
                                                            .slice()
                                                            .sort((a, b) => b.version - a.version)
                                                            .map((v) => (
                                                                <option key={v.version} value={v.version}>
                                                                    v{v.version}
                                                                    {v.version === template.currentVersion ? ' (최신)' : ''}
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>
                                            ) : template.currentVersion ? (
                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 gap-0.5">
                                                    v{template.currentVersion}
                                                </Badge>
                                            ) : null}
                                            <button
                                                type="button"
                                                onClick={() => onViewVersionHistory(template)}
                                                className="text-[10px] text-orange-500 hover:text-orange-600 hover:underline flex items-center gap-0.5"
                                            >
                                                <History className="h-3 w-3" />
                                                이력 보기
                                            </button>
                                        </div>
                                    </div>

                                    {/* 액션 버튼들 */}
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <Button
                                            variant="default"
                                            size="sm"
                                            className="h-8 gap-1 bg-orange-500 hover:bg-orange-600"
                                            onClick={() => onSignWithTemplate(String(template.id))}
                                            title="동의 받기"
                                        >
                                            <PenTool className="h-4 w-4" />
                                            동의 받기
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => onViewTemplate(template)}
                                            title="보기"
                                        >
                                            <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => onEditTemplate(template)}
                                            title="수정"
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => onDuplicateTemplate(template)}
                                            title="복제"
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-danger-500 hover:text-danger-600"
                                            onClick={() => onDeleteTemplate(template)}
                                            title="삭제"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* 페이지네이션 */}
                {pageInfo && pageInfo.totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t">
                        <div className="text-sm text-neutral-500 dark:text-neutral-400">
                            전체 {pageInfo.totalElements}개 중 {pageInfo.currentPage * pageInfo.pageSize + 1}-
                            {Math.min((pageInfo.currentPage + 1) * pageInfo.pageSize, pageInfo.totalElements)}개 표시
                        </div>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={onGoToFirstPage}
                                disabled={pageInfo.isFirst}
                                title="첫 페이지"
                            >
                                <ChevronsLeft className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={onGoToPrevPage}
                                disabled={!pageInfo.hasPrev}
                                title="이전 페이지"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <div className="flex items-center gap-1 mx-2">
                                {Array.from({ length: Math.min(5, pageInfo.totalPages) }, (_, i) => {
                                    let pageNum: number;
                                    if (pageInfo.totalPages <= 5) {
                                        pageNum = i;
                                    } else if (pageInfo.currentPage < 3) {
                                        pageNum = i;
                                    } else if (pageInfo.currentPage > pageInfo.totalPages - 4) {
                                        pageNum = pageInfo.totalPages - 5 + i;
                                    } else {
                                        pageNum = pageInfo.currentPage - 2 + i;
                                    }
                                    return (
                                        <Button
                                            key={pageNum}
                                            variant={pageInfo.currentPage === pageNum ? 'default' : 'outline'}
                                            size="sm"
                                            className={cn(
                                                'h-8 w-8 p-0',
                                                pageInfo.currentPage === pageNum && 'bg-orange-500 hover:bg-orange-600'
                                            )}
                                            onClick={() => onGoToPage?.(pageNum)}
                                        >
                                            {pageNum + 1}
                                        </Button>
                                    );
                                })}
                            </div>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={onGoToNextPage}
                                disabled={!pageInfo.hasNext}
                                title="다음 페이지"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={onGoToLastPage}
                                disabled={pageInfo.isLast}
                                title="마지막 페이지"
                            >
                                <ChevronsRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {templates.length === 0 && (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <FileText className="mx-auto h-12 w-12 text-neutral-300" />
                            <p className="mt-4 text-neutral-500 dark:text-neutral-400">등록된 동의서 양식이 없습니다.</p>
                            <Button className="mt-4 gap-2" onClick={() => onCreateTemplate()}>
                                <Plus className="h-4 w-4" />
                                첫 번째 양식 만들기
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* 안내 메시지 */}
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-700">
                    <span className="font-semibold">💡 서명된 동의서 조회</span><br />
                    고객이 서명한 동의서는 <span className="font-medium">고객 관리 {'>'} 사용자 상세 모달</span>에서 확인할 수 있습니다.
                </p>
            </div>
        </div>
    );
}
